import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTheme } from "@mui/material";
import { GroundingRecord } from "../utils/backend-utils";

interface GroundingProceedDialogProps {
  open: boolean;
  grounding?: GroundingRecord | null;
  onProceed: () => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog shown when the user clicks "Submit Changes and Start
 * Plan" on a session whose grounding is 'partial' or 'none'. Requires an
 * explicit "Proceed with LLM-only plan" click so the user can't blow through
 * by habit.
 */
export function GroundingProceedDialog({
  open, grounding, onProceed, onCancel,
}: GroundingProceedDialogProps) {
  const theme = useTheme();
  if (!grounding) return null;
  const isNone = grounding.status === "none";

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ErrorOutlineIcon sx={{ color: theme.palette.error.main }} />
        Proceed without authoritative docs?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {isNone ? (
            <>None of the configured source pages loaded. The plan below is <strong>LLM-improvised</strong> and not verified against vendor documentation.</>
          ) : (
            <>Some pages failed to load. Plan steps that depended on them are <strong>LOW confidence</strong>.</>
          )}
        </DialogContentText>
        {grounding.urls_failed && grounding.urls_failed.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
              Failed to load:
            </Typography>
            {grounding.urls_failed.map((u) => (
              <Typography
                key={u}
                variant="caption"
                sx={{ display: "block", fontFamily: "monospace", fontSize: "0.7rem", wordBreak: "break-all" }}
              >
                {u}
              </Typography>
            ))}
          </Box>
        )}
        <DialogContentText>
          If you want better grounding, you can cancel and run the local
          doc-scraper script (<code>scripts/scrape_tableau_docs.py</code>) to
          bundle these pages into the lambda. Otherwise, you can proceed and
          verify each step manually against your own references.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onProceed}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Proceed with LLM-only plan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
